// Client-supplied legal texts, published in English for every language until approved
// translations exist. Bracketed variables are filled in and drafting notes addressed to the
// developer or counsel are removed; everything else is the client's wording.
// - Website Terms of Use and Speed & Fair Usage Policy: Revised Legal Package v3 (15 Oct 2026).
// - Cookie Policy: Cookie Policy build note (13 Oct 2026), with the company name from the
//   package and the choices matching the 15 Oct cookie settings design.

const COMPANY = 'Jasper Group Corporation Limited, trading as Roam4Less';
const LAST_UPDATED = '16 October 2026';

// Shown at the end of every legal page (client request, 20 Oct 2026).
export const COMPANY_DETAILS = {
  name: 'JASPER Group Corporation Ltd',
  address: '917A, Block A, 9/F, New Mandarin Plaza, 14 Science Museum Road, Tsim Sha Tsui, Hong Kong SAR',
  registration: 'Reg# 76669577',
};

export const websiteTermsOfUse = {
  title: 'Website Terms of Use',
  updated: LAST_UPDATED,
  sections: [
    {
      heading: 'Scope',
      paragraphs: [
        `This website is operated by ${COMPANY}. These terms cover browsing and website content; eSIM purchases are additionally governed by the Terms & Conditions.`,
      ],
    },
    {
      heading: 'Permitted use',
      paragraphs: [
        'Users may browse and use the website lawfully. Unauthorized access, disruption, fraud, malicious scraping and impersonation are prohibited.',
      ],
    },
    {
      heading: 'Content and intellectual property',
      paragraphs: [
        'Website copy, logos, imagery and software are owned or licensed by us. Reuse is restricted except as permitted by law or written authorization.',
      ],
    },
    {
      heading: 'Accuracy and external links',
      paragraphs: [
        'We aim for accurate website content; binding product specifications and final prices are displayed at checkout. External links are for convenience and are not endorsements.',
      ],
    },
    {
      heading: 'Mandatory rights and contact',
      paragraphs: [
        'Nothing in these terms restricts non-excludable rights. Contact contact@roam4less.app. Hong Kong law applies only insofar as mandatory consumer protections permit.',
      ],
    },
  ],
};

export const speedAndFairUsagePolicy = {
  title: 'Speed & Fair Usage Policy',
  updated: LAST_UPDATED,
  sections: [
    {
      heading: 'Data Bundles — our full-speed promise',
      paragraphs: [
        'The purchased bundle quantity is its high-speed data allowance. Roam4Less does not impose a hidden lower usage threshold after which bundle data is intentionally throttled. Actual speeds vary by local operator, device, coverage, network congestion and network management. Once allowance is exhausted, you can buy a top-up from the Roam4Less app or website.',
      ],
    },
    {
      heading: 'Unlimited Plans — transparency',
      paragraphs: [
        'Unlimited usage is not necessarily unlimited full-speed usage. Each plan page discloses before purchase: full-speed allowance 1 GB per 24 hours, speed after threshold up to 512 Kbps (up to 100 Kbps after 1.5 GB in the same period), reset period 24 hours, validity, hotspot rules and any traffic restrictions.',
      ],
    },
    {
      heading: 'Universal eSIM',
      paragraphs: [
        'The actual included-country list, data allowance, speed rules and validity applicable to the selected Universal eSIM are displayed for that plan. The Data Bundle speed promise does not automatically extend to this product if it has different terms.',
      ],
    },
    {
      heading: 'Network and device factors',
      paragraphs: [
        '4G/5G and actual throughput depend on local network availability, congestion, device compatibility, radio conditions and operator management. We do not guarantee a fixed Mbps speed unless explicitly sold as such.',
      ],
    },
    {
      heading: 'Disputes and changes',
      paragraphs: [
        'A customer who receives materially different service may contact support@roam4less.app and invoke applicable contractual and statutory remedies. Any material plan restriction must be shown before checkout, not added after purchase.',
      ],
    },
  ],
};

const cookieColumns = ['Cookie', 'Provider', 'First / third party', 'Purpose', 'Duration'];

export const cookiePolicy = {
  title: 'Cookie Policy',
  updated: LAST_UPDATED,
  sections: [
    {
      heading: '1. Scope of this policy',
      paragraphs: [
        `This Cookie Policy explains how ${COMPANY} (“Roam4Less”, “we”, “us” or “our”) uses cookies and similar technologies on roam4less.app (the “Website”). It complements our Privacy Policy, which explains how we process personal data more generally.`,
      ],
    },
    {
      heading: '2. Summary',
      bullets: [
        'We use strictly necessary cookies that our checkout needs to take payments securely. They don’t require your consent.',
        'We use affiliate cookies only if you accept Advertising & Marketing cookies. They tell us which partner referred you.',
        'We don’t use analytics or advertising cookies.',
        'You can accept or reject optional cookies when you first visit, and change your choice at any time via “Cookie settings” in the footer.',
      ],
    },
    {
      heading: '3. What are cookies?',
      paragraphs: [
        'Cookies are small text files that a website stores on your device when you visit it. They let the website recognise your device and remember information about your visit. Similar technologies, such as local storage, work in a similar way, and this policy covers them too.',
        'Cookies can be:',
      ],
      bullets: [
        'First-party: set by the website you are visiting (roam4less.app), or third-party: set by another company whose service runs on the website (for example, our payment provider).',
        'Session: deleted when you close your browser, or persistent: kept on your device for a set period or until you delete them.',
      ],
    },
    {
      heading: '4. Why we use cookies',
      paragraphs: ['We use cookies to:'],
      bullets: [
        'take payments securely and prevent payment fraud',
        'credit our partners when you reach us through their link (only with your consent)',
      ],
    },
    {
      heading: '5.1 Strictly necessary cookies',
      paragraphs: [
        'Our checkout can’t take payments without these cookies. They are set by our payment provider, Stripe, and the bot protection it uses, to process payments securely and prevent fraud. Because they are strictly necessary, they don’t require your consent.',
      ],
      table: {
        columns: cookieColumns,
        rows: [
          ['__stripe_mid', 'Stripe', 'First-party (set by Stripe)', 'Payment fraud prevention', '1 year'],
          ['__stripe_sid', 'Stripe', 'First-party (set by Stripe)', 'Payment fraud prevention', '30 minutes'],
          ['m', 'Stripe', 'Third-party (m.stripe.network)', 'Payment fraud prevention', 'About 13 months'],
          ['__cf_bm', 'hCaptcha (loaded by Stripe)', 'Third-party', 'Bot detection', '30 minutes'],
          ['__cflb', 'hCaptcha (loaded by Stripe)', 'Third-party', 'Load balancing', '30 minutes'],
          ['hmt_id', 'hCaptcha (loaded by Stripe)', 'Third-party', 'Bot detection', 'About 30 days'],
        ],
      },
      after: ['We also store your cookie choice on your device, so we don’t have to ask you again on every visit.'],
    },
    {
      heading: '5.2 Google Pay cookies (checkout page only)',
      paragraphs: [
        'If you pay with Google Pay, Google sets the following cookies when Google Pay loads on our checkout page. They are not set on any other page.',
      ],
      table: {
        columns: cookieColumns,
        rows: [
          ['SEARCH_SAMESITE', 'Google (Google Pay)', 'Third-party', 'Security', '6 months'],
          ['__Secure-STRP', 'Google (Google Pay)', 'Third-party', 'Set by Google when Google Pay loads', 'Short-lived'],
          ['NID', 'Google (Google Pay)', 'Third-party', 'Set by Google when Google Pay loads', '6 months'],
        ],
      },
    },
    {
      heading: '5.3 Affiliate cookies (only with your consent)',
      paragraphs: [
        'If you reach the Website through a partner’s link (for example, a travel creator or a comparison website), these cookies let us credit that partner if you make a purchase. They are only set if you accept Advertising & Marketing cookies.',
      ],
      table: {
        columns: cookieColumns,
        rows: [
          ['_fprom_tid', 'FirstPromoter', 'First-party (set by FirstPromoter)', 'Affiliate attribution', '60 days'],
          ['_fprom_ref', 'FirstPromoter', 'First-party (set by FirstPromoter)', 'Affiliate attribution', '60 days'],
        ],
      },
      after: ['If you reject them: nothing changes for you. You can still browse and buy normally; the partner may simply not be credited for your purchase.'],
    },
    {
      heading: '6. Third-party providers',
      paragraphs: ['Some of the cookies above are set by third parties. They process data under their own privacy policies:'],
      table: {
        columns: ['Provider', 'What it does for us', 'Privacy policy'],
        rows: [
          ['Stripe', 'Payment processing and fraud prevention', 'https://stripe.com/privacy'],
          ['hCaptcha', 'Bot protection (loaded by Stripe)', 'https://www.hcaptcha.com/privacy'],
          ['Google', 'Google Pay at checkout', 'https://policies.google.com/privacy'],
          ['FirstPromoter', 'Affiliate attribution', 'https://firstpromoter.com/privacy'],
        ],
      },
      after: ['Some of these providers are based in, or may process data in, the United States. See our Privacy Policy for details.'],
    },
    {
      heading: '7. Your choices',
      paragraphs: [
        'When you first visit the Website, a banner lets you accept or reject optional cookies, or choose categories and save your preferences. Strictly necessary cookies are always active.',
        'You can change or withdraw your consent at any time by clicking “Cookie settings” in the footer of any page. Withdrawing consent doesn’t affect anything done before you withdrew it. Cookies already stored on your device remain until they expire or you delete them (see section 8).',
      ],
    },
    {
      heading: '8. Disabling and deleting cookies in your browser',
      paragraphs: ['You can also block or delete cookies in your browser settings:'],
      bullets: ['Chrome', 'Safari', 'Firefox', 'Edge'],
      after: ['If you block strictly necessary cookies, payments and checkout may not work.'],
    },
    {
      heading: '9. Our mobile apps',
      paragraphs: ['Our iOS and Android apps don’t use cookies or analytics and tracking technologies.'],
    },
    {
      heading: '10. Changes to this policy',
      paragraphs: [
        'We may update this policy to reflect changes to the Website, our providers or the law. We’ll publish the new version on this page and update the “Last updated” date. If we start using new types of cookies, we’ll ask for your consent first where required.',
      ],
    },
    {
      heading: '11. Contact',
      paragraphs: ['If you have questions about this policy or how we use cookies, contact us:'],
      lines: [
        COMPANY,
        'Unit 917A, Block A, 9/F, New Mandarin Plaza, 14 Science Museum Road, Tsim Sha Tsui, Hong Kong SAR',
        'Reg. 76669577',
        'Email: contact@roam4less.app',
      ],
    },
  ],
};
